/**
 * f80Prefill — the ONE mapping between the applicant's pre-lodgement partner-form
 * data and the Form 80 (F80_character) supplementary form.
 *
 * Two consumers, one source of truth:
 *   1. The client-facing Form 80 tab (src/pages/Form80.tsx): mirrors + seeds.
 *   2. The Form 80 PDF working copy (v1FormDataMapper.mapF80 nests this module's
 *      flat output for api/render-pdf).
 *
 * THE DISPOSITION MODEL (Tzvi ruling 2026-07-21: Form 80 stays SEPARATE from
 * pre-lodgement; prefill everything derivable, ask the client only the gaps):
 *
 *   - MIRROR fields (schema `read_only: true` on F80_character.json): recomputed
 *     from the pre-lodgement intake on every load and NEVER persisted to the F80
 *     row (pruneReadOnlyPaths strips them before save). The pre-lodgement intake
 *     stays the single source of truth; the two forms cannot drift.
 *   - SEED-ONCE fields (F80_SEED_KEYS): repeaters the client must extend beyond
 *     pre-lodgement fidelity (e.g. travel needs a per-trip reason, address history
 *     needs short stays). Seeded into the F80 row when it is first created, then
 *     client-owned.
 *   - Everything else: genuine Form-80-only questions the client (or staff, for
 *     client_hidden parts) answers directly in the tab.
 *
 * All source reads go through the EXPANDED (nested) state via readNestedPath, so
 * both storage shapes work: top-level autosave blobs AND flat dotted seed rows.
 * (The previous mapF80 read flat dotted keys only, which silently missed every
 * blob-stored answer in production.)
 */

// NOTE the .js suffix: this module is imported by api/render-pdf.ts, which
// runs as a real Node ESM function on Vercel where extensionless relative
// imports fail at runtime (same convention as stageCompletion.ts).
import { readNestedPath, expandFormState } from './formStateAdapter.js';
import type { FormState } from './formStateAdapter.js';
import type { FieldDef, FormSchema, SectionDef } from '../schema-renderer/types';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

function arr(v: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(v)) return [];
  return v.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object');
}

/** Render a schema `address` object as a single Form 80 free-text address line. */
export function formatAddressText(v: unknown): string | undefined {
  if (!v) return undefined;
  if (typeof v === 'string') return str(v);
  if (typeof v !== 'object' || Array.isArray(v)) return undefined;
  const a = v as Record<string, unknown>;
  const parts = [a.line1, a.line2, a.suburb, a.state, a.postcode, a.country]
    .map(str)
    .filter((x): x is string => !!x);
  return parts.length ? parts.join(', ') : undefined;
}

// ---------------------------------------------------------------------------
// Character battery: F80 Q36 sub-path -> partner-form yn canonical
// ---------------------------------------------------------------------------

/**
 * The 18 mapped ImmiAccount character questions (Q36c family violence has no
 * partner-form equivalent and stays a client-answered gap in the tab).
 */
export const F80_CHARACTER_MAP: Array<[string, string]> = [
  ['q36a_charged_offence_awaiting', 'applicant.character.criminal_charges_pending'],
  ['q36b_convicted_offence', 'applicant.character.criminal_conviction'],
  ['q36d_subject_of_protection_order', 'applicant.character.domestic_violence_order'],
  ['q36e_arrest_warrant_or_interpol', 'applicant.character.arrest_warrant_or_interpol'],
  ['q36f_sexual_offence_against_child', 'applicant.character.child_sexual_offence'],
  ['q36g_sex_offender_register', 'applicant.character.sex_offender_register'],
  ['q36h_acquitted_unsoundness', 'applicant.character.acquitted_by_insanity'],
  ['q36i_not_fit_to_plead', 'applicant.character.not_fit_to_plead'],
  ['q36j_national_security_risk', 'applicant.character.national_security_risk'],
  ['q36k_war_crimes_etc', 'applicant.character.genocide_war_crimes'],
  ['q36l_associated_criminal_org', 'applicant.character.criminal_associations'],
  ['q36m_associated_violent_org', 'applicant.character.violence_organisation'],
  ['q36n_military_or_intel_service', 'applicant.character.military_police_intelligence_service'],
  ['q36o_military_paramilitary_training', 'applicant.character.military_or_weapons_training'],
  ['q36p_people_smuggling', 'applicant.character.people_smuggling_trafficking'],
  ['q36q_removed_deported', 'applicant.character.removed_or_deported'],
  ['q36r_overstayed_visa', 'applicant.character.overstayed_visa'],
  ['q36s_outstanding_au_debts', 'applicant.character.outstanding_debts_au'],
];

/**
 * Per-question detail repeaters on the partner form. Composed into the single
 * F80 "details for Yes answers" block (the official form has one shared box).
 */
const CHARACTER_DETAIL_SOURCES: Array<[string, string]> = [
  ['Charged with an offence awaiting legal action', 'applicant.character.criminal_charges_pending_details'],
  ['Convicted of an offence', 'applicant.character.criminal_conviction_details'],
  ['Domestic violence or protection order', 'applicant.character.domestic_violence_order_details'],
  ['Arrest warrant or Interpol notice', 'applicant.character.arrest_warrant_or_interpol_details'],
  ['Sexually based offence involving a child', 'applicant.character.child_sexual_offence_details'],
  ['Sex offender register', 'applicant.character.sex_offender_register_details'],
  ['Acquitted on grounds of unsoundness of mind', 'applicant.character.acquitted_by_insanity_details'],
  ['Found not fit to plead', 'applicant.character.not_fit_to_plead_details'],
  ['National security risk', 'applicant.character.national_security_risk_details'],
  ['Genocide / war crimes / crimes against humanity', 'applicant.character.genocide_war_crimes_details'],
  ['Association with criminal conduct', 'applicant.character.criminal_associations_details'],
  ['Association with violent organisation', 'applicant.character.violence_organisation_details'],
  ['Military / police / intelligence service', 'applicant.character.military_police_intelligence_service_details'],
  ['Military or weapons training', 'applicant.character.military_or_weapons_training_details'],
  ['People smuggling or trafficking', 'applicant.character.people_smuggling_trafficking_details'],
  ['Removed or deported', 'applicant.character.removed_or_deported_details'],
  ['Overstayed a visa', 'applicant.character.overstayed_visa_details'],
  ['Outstanding debts to the Australian Government', 'applicant.character.outstanding_debts_au_details'],
];

function composeCharacterDetails(read: (p: string) => unknown): string | undefined {
  const blocks: string[] = [];
  for (const [heading, path] of CHARACTER_DETAIL_SOURCES) {
    const entries = arr(read(path));
    if (!entries.length) continue;
    const lines = entries.map((e) => {
      const bits = Object.entries(e)
        .map(([k, v]) => {
          const s = str(v);
          return s ? `${k.replace(/_/g, ' ')}: ${s}` : null;
        })
        .filter(Boolean);
      return `- ${bits.join('; ')}`;
    });
    blocks.push(`${heading}:\n${lines.join('\n')}`);
  }
  return blocks.length ? blocks.join('\n\n') : undefined;
}

// ---------------------------------------------------------------------------
// Citizenship helper
// ---------------------------------------------------------------------------

function citizenshipList(read: (p: string) => unknown): string[] {
  const list: string[] = [];
  const isCitizenOfPassport = str(read('applicant.citizenship.is_citizen_of_passport_country'));
  const passportCountry = str(read('applicant.passport.country'));
  // The passport country is the primary citizenship signal unless the applicant
  // explicitly said they are NOT a citizen of it.
  if (passportCountry && isCitizenOfPassport !== 'No') list.push(passportCountry);
  const others = read('applicant.citizenship.other_countries');
  if (Array.isArray(others)) {
    for (const c of others) {
      const s = str(c);
      if (s) list.push(s);
    }
  }
  return list.filter((c, i) => list.indexOf(c) === i);
}

// ---------------------------------------------------------------------------
// MIRROR fields — recomputed every load, never persisted to the F80 row
// ---------------------------------------------------------------------------

/**
 * Build the flat mirror FormState (F80 canonical -> value) from the applicant's
 * partner-form state. Accepts either the flat row map or an already-expanded
 * nested state. Only writes keys flagged `read_only` in F80_character.json —
 * f80Prefill.test.ts pins that invariant.
 */
export function buildF80Prefill(source: FormState): FormState {
  const src = expandFormState(source);
  const read = (p: string) => readNestedPath(src, p);
  const out: FormState = {};
  const set = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  };

  // --- Part A: identity ----------------------------------------------------
  set('applicant.name.family_name', str(read('applicant.name.family_name')));
  set('applicant.name.given_names', str(read('applicant.name.given_names')));
  set('applicant.sex', str(read('applicant.sex')));
  set('applicant.dob', str(read('applicant.dob')));
  set('applicant.birth.town_city', str(read('applicant.place_of_birth.town')));
  set('applicant.birth.state_province', str(read('applicant.place_of_birth.state_province')));
  set('applicant.birth.country', str(read('applicant.place_of_birth.country')));

  const ccc = str(read('applicant.chinese_commercial_code'));
  set('applicant.chinese_commercial_code', ccc);
  // The partner form always offers the CCC field; absence after a completed
  // pre-lodgement intake genuinely means "not used".
  const hasAnyIdentity = !!str(read('applicant.name.family_name')) || !!str(read('applicant.passport.number'));
  if (hasAnyIdentity) set('applicant.uses_chinese_commercial_code', ccc ? 'Yes' : 'No');

  set('applicant.has_other_names', str(read('applicant.other_names.has_other_names')));
  const otherNames = arr(read('applicant.other_names.entries'));
  if (otherNames.length) {
    set(
      'applicant.other_names',
      otherNames.map((e) => {
        const mapped: Record<string, unknown> = {};
        if (str(e.family_name)) mapped.family_name = str(e.family_name);
        if (str(e.given_names)) mapped.given_names = str(e.given_names);
        if (str(e.reason)) mapped.name_type = str(e.reason);
        return mapped;
      }),
    );
  }

  // Citizenship summary fields moved to SEEDS (2026-07-21 review): an
  // applicant who is not a citizen of their passport country with no other
  // countries listed would otherwise face locked, permanently empty required
  // fields. Seeded when derivable, client-editable always.

  // Partner visas: the sponsor IS the partner.
  if (hasAnyIdentity) set('applicant.has_partner', 'Yes');

  // --- Part B: passports -----------------------------------------------------
  if (str(read('applicant.passport.number'))) set('applicant.has_current_passport', 'Yes');
  set('applicant.passport.number', str(read('applicant.passport.number')));
  set('applicant.passport.country', str(read('applicant.passport.country')));
  set('applicant.passport.issue_date', str(read('applicant.passport.date_of_issue')));
  set('applicant.passport.expiry_date', str(read('applicant.passport.date_of_expiry')));
  set('applicant.passport.nationality', str(read('applicant.passport.nationality')));
  set('applicant.passport.place_of_issue', str(read('applicant.passport.place_of_issue')));

  set('applicant.has_other_passports', str(read('applicant.other_passports.has_other_passports')));
  const otherPassports = arr(read('applicant.other_passports.entries'));
  if (otherPassports.length) {
    set(
      'applicant.other_passports',
      otherPassports.map((e) => {
        const mapped: Record<string, unknown> = {};
        if (str(e.number)) mapped.number = str(e.number);
        if (str(e.country)) mapped.country = str(e.country);
        if (str(e.date_of_issue)) mapped.issue_date = str(e.date_of_issue);
        if (str(e.date_of_expiry)) mapped.expiry_date = str(e.date_of_expiry);
        if (str(e.nationality)) mapped.nationality = str(e.nationality);
        if (str(e.place_of_issue)) mapped.place_of_issue = str(e.place_of_issue);
        return mapped;
      }),
    );
  }
  set('applicant.has_previous_passports', str(read('applicant.expired_passports.has_expired_passports')));

  // --- Part D: contact -------------------------------------------------------
  const email = str(read('applicant.email'));
  if (email) set('applicant.has_email', 'Yes');
  set('applicant.email', email);
  const phoneMobile = str(read('applicant.phone_mobile'));
  const phoneHome = str(read('applicant.phone_home'));
  const phoneWork = str(read('applicant.phone_business'));
  if (phoneMobile || phoneHome || phoneWork) set('applicant.has_phone', 'Yes');
  set('applicant.phone.mobile', phoneMobile);
  set('applicant.phone.home', phoneHome);
  set('applicant.phone.work', phoneWork);

  // --- Part E gate -----------------------------------------------------------
  set('applicant.has_international_travel_10y', str(read('applicant.countries_visited.has_visited')));

  // --- Parts F/G: identical canonicals on both forms --------------------------
  const employment = arr(read('applicant.employment_history_lifetime'));
  if (employment.length) set('applicant.employment_history_lifetime', employment);
  const education = arr(read('applicant.tertiary_education'));
  if (education.length) set('applicant.tertiary_education', education);

  // --- Part J: current-visa fragments -----------------------------------------
  set('applicant.au_visa.current_arrival_date', str(read('applicant.visa_history.date_of_last_entry')));

  // --- Part K: character battery ----------------------------------------------
  for (const [subPath, srcKey] of F80_CHARACTER_MAP) {
    set(`applicant.character.${subPath}`, str(read(srcKey)));
  }
  set('applicant.character.details_for_yes_answers', composeCharacterDetails(read));

  // --- Part O: deportations ------------------------------------------------------
  set('applicant.deportations.has_been_deported', str(read('applicant.character.removed_or_deported')));

  // Part N refusals moved to SEEDS (2026-07-21 review): the 300 PMV form has
  // no refusal source canonicals, so a mirror would lock the required gate
  // permanently empty for every PMV client.

  return out;
}

// ---------------------------------------------------------------------------
// SEED-ONCE fields — written into the F80 row on first bootstrap only
// ---------------------------------------------------------------------------

/** Top-level F80 canonical keys treated as seed-once (client-owned after seeding). */
export const F80_SEED_KEYS = [
  'applicant.citizenship_status',
  'applicant.current_citizenship.country',
  'applicant.has_other_citizenships',
  'applicant.refusals.has_visa_refusal',
  'applicant.refusals.visa_refusal_details',
  'applicant.address_history_10y',
  'applicant.international_travel_10y',
  'applicant.other_citizenships',
  'applicant.children',
  'applicant.parents',
  'applicant.siblings',
  'applicant.has_children',
  'applicant.has_parents_details',
  'applicant.has_siblings',
  'applicant.has_national_id_documents',
  'applicant.national_id_documents',
  'applicant.military.has_served',
  'applicant.military.service_history',
] as const;

const CHILD_RELATIONSHIPS = new Set(['Child', 'Step Child']);
const PARENT_RELATIONSHIPS = new Set(['Parent', 'Step Parent']);
const SIBLING_RELATIONSHIPS = new Set(['Brother', 'Sister', 'Step-brother', 'Step-sister']);

function familyEntry(e: Record<string, unknown>): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};
  if (str(e.relationship_to_applicant)) mapped.relationship = str(e.relationship_to_applicant);
  if (str(e.family_name)) mapped.family_name = str(e.family_name);
  if (str(e.given_names)) mapped.given_names = str(e.given_names);
  if (str(e.sex)) mapped.sex = str(e.sex);
  if (str(e.dob)) mapped.dob = str(e.dob);
  const residence = str(e.is_deceased) === 'Yes' ? 'DECEASED' : str(e.country_of_residence);
  if (residence) mapped.country_of_residence = residence;
  return mapped;
}

/**
 * Build the flat seed FormState from the applicant's partner-form state.
 * Only keys in F80_SEED_KEYS are ever written — pinned by f80Prefill.test.ts.
 */
export function buildF80Seeds(source: FormState): FormState {
  const src = expandFormState(source);
  const read = (p: string) => readNestedPath(src, p);
  const out: FormState = {};
  const set = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  };

  // Q17 address history: current residential address first, then the >12-month
  // country stays. The client adds short stays / share houses in the tab.
  const addressRows: Array<Record<string, unknown>> = [];
  const residential = formatAddressText(read('applicant.residential_address'));
  if (residential) {
    const row: Record<string, unknown> = { date_to: 'CURRENT', full_address: residential };
    const resCountry = str(readNestedPath(src, 'applicant.residential_address.country'));
    if (resCountry) row.country = resCountry;
    addressRows.push(row);
  }
  for (const e of arr(read('applicant.countries_resided.entries'))) {
    const row: Record<string, unknown> = {};
    if (str(e.date_from)) row.date_from = str(e.date_from);
    row.date_to = str(e.date_to) ?? 'CURRENT';
    const addressText = formatAddressText(e.address) ?? formatAddressText(e.last_permanent_address);
    if (addressText) row.full_address = addressText;
    if (str(e.country)) row.country = str(e.country);
    addressRows.push(row);
  }
  set('applicant.address_history_10y', addressRows);

  // Q18 travel: per-trip rows; the reason column is the Form-80-only gap the
  // client completes in the tab.
  set(
    'applicant.international_travel_10y',
    arr(read('applicant.countries_visited.entries')).map((e) => {
      const row: Record<string, unknown> = {};
      if (str(e.country)) row.country = str(e.country);
      if (str(e.date_from)) row.date_from = str(e.date_from);
      if (str(e.date_to)) row.date_to = str(e.date_to);
      return row;
    }),
  );

  // Q6/Q7 citizenship summary: seeded when derivable, editable always (an
  // applicant who is not a citizen of their passport country needs to answer
  // these directly). The other-citizenships how-gained/date columns are gaps.
  const citizenships = citizenshipList(read);
  if (citizenships.length > 0) {
    set('applicant.citizenship_status', 'Has citizenship');
    set('applicant.current_citizenship.country', citizenships[0]);
    set('applicant.has_other_citizenships', citizenships.length > 1 ? 'Yes' : 'No');
  }
  set(
    'applicant.other_citizenships',
    citizenships.slice(1).map((c) => ({ country: c })),
  );

  // Part N refusals: no source on the 300 PMV form, and clients may need to
  // add refusals from other countries, so these stay editable after seeding.
  const refusedOrCancelled = str(read('applicant.visa_history.previous_visa_refused_or_cancelled'));
  const refusedEntry = str(read('applicant.visa_history.refused_entry_or_visa'));
  if (refusedOrCancelled === 'Yes' || refusedEntry === 'Yes') {
    set('applicant.refusals.has_visa_refusal', 'Yes');
  } else if (refusedOrCancelled === 'No' && refusedEntry === 'No') {
    set('applicant.refusals.has_visa_refusal', 'No');
  }
  const refusalEntries = arr(read('applicant.visa_history.refusal_details'));
  if (refusalEntries.length) {
    const lines = refusalEntries.map((e) => {
      const bits = [
        str(e.visa_number) ? `visa number: ${str(e.visa_number)}` : null,
        str(e.date_of_refusal) ? `date: ${str(e.date_of_refusal)}` : null,
        str(e.reason) ? `reason: ${str(e.reason)}` : null,
      ].filter(Boolean);
      return `- ${bits.join('; ')}`;
    });
    set('applicant.refusals.visa_refusal_details', lines.join('\n'));
  }

  // Q43-45: split the immediate-family blocks by relationship. In-laws are not
  // asked on Form 80 and are skipped.
  const family = arr(read('applicant.immediate_family.entries'));
  const children = family.filter((e) => CHILD_RELATIONSHIPS.has(str(e.relationship_to_applicant) ?? '')).map(familyEntry);
  const parents = family.filter((e) => PARENT_RELATIONSHIPS.has(str(e.relationship_to_applicant) ?? '')).map(familyEntry);
  const siblings = family.filter((e) => SIBLING_RELATIONSHIPS.has(str(e.relationship_to_applicant) ?? '')).map(familyEntry);
  set('applicant.children', children);
  set('applicant.parents', parents);
  set('applicant.siblings', siblings);
  if (children.length) set('applicant.has_children', 'Yes');
  if (parents.length) set('applicant.has_parents_details', 'Yes');
  if (siblings.length) set('applicant.has_siblings', 'Yes');

  // Q14 identity documents: national ID card + the other-identity-documents list.
  const idDocs: Array<Record<string, unknown>> = [];
  if (str(read('applicant.national_id.has_card')) === 'Yes') {
    const row: Record<string, unknown> = { type: 'National identity card' };
    if (str(read('applicant.national_id.country_of_issue'))) row.country_of_issue = str(read('applicant.national_id.country_of_issue'));
    if (str(read('applicant.national_id.identification_number'))) row.id_number = str(read('applicant.national_id.identification_number'));
    idDocs.push(row);
  }
  for (const e of arr(read('applicant.other_identity_documents.entries'))) {
    const row: Record<string, unknown> = {};
    if (str(e.document_type)) row.type = str(e.document_type);
    if (str(e.country_of_issue)) row.country_of_issue = str(e.country_of_issue);
    if (str(e.identification_number)) row.id_number = str(e.identification_number);
    idDocs.push(row);
  }
  set('applicant.national_id_documents', idDocs);
  if (idDocs.length) {
    set('applicant.has_national_id_documents', 'Yes');
  } else if (
    str(read('applicant.national_id.has_card')) === 'No' &&
    str(read('applicant.other_identity_documents.has_documents')) === 'No'
  ) {
    set('applicant.has_national_id_documents', 'No');
  }

  // Q37 military service: seed from the character service repeater (unit/rank/
  // dates); duties + country of deployment are the Form-80-only columns.
  const service = arr(read('applicant.character.military_police_intelligence_service_details'));
  if (service.length) {
    set('applicant.military.has_served', 'Yes');
    set(
      'applicant.military.service_history',
      service.map((e) => {
        const row: Record<string, unknown> = {};
        if (str(e.date_from)) row.date_from = str(e.date_from);
        if (str(e.date_to)) row.date_to = str(e.date_to);
        if (str(e.country_of_service)) row.country_of_service = str(e.country_of_service);
        if (str(e.unit)) row.unit_name = str(e.unit);
        if (str(e.rank)) row.rank = str(e.rank);
        return row;
      }),
    );
  }

  return out;
}

// ---------------------------------------------------------------------------
// Schema-driven utilities shared by the tab and the PDF path
// ---------------------------------------------------------------------------

function collectFromFields(fields: FieldDef[] | undefined, out: string[]): void {
  for (const f of fields ?? []) {
    if (f.read_only) out.push(f.canonical_name);
  }
}

/** Canonical names of every top-level `read_only: true` field in a schema. */
export function collectReadOnlyCanonicals(schema: FormSchema): string[] {
  const out: string[] = [];
  for (const section of schema.sections as SectionDef[]) {
    collectFromFields(section.fields, out);
    for (const sub of section.subsections ?? []) collectFromFields(sub.fields, out);
  }
  return out;
}

/**
 * Remove the given canonical paths from a NESTED form state (RHF getValues()
 * shape). Used by the Form 80 tab's save path so mirror values are never
 * persisted — they are re-derived from pre-lodgement data on every load.
 * Returns a deep-pruned copy; the input is not mutated.
 */
export function pruneReadOnlyPaths(state: FormState, canonicals: string[]): FormState {
  const clone = structuredClone(state) as Record<string, unknown>;
  for (const path of canonicals) {
    const parts = path.split('.');
    let cur: Record<string, unknown> | undefined = clone;
    for (let i = 0; i < parts.length - 1 && cur; i++) {
      const next: unknown = cur[parts[i]];
      cur = next && typeof next === 'object' && !Array.isArray(next)
        ? (next as Record<string, unknown>)
        : undefined;
    }
    if (cur) delete cur[parts[parts.length - 1]];
    // Flat dotted key fallback (seed-row shape).
    delete clone[path];
  }
  return clone;
}

/**
 * Overlay the mirror values onto a nested state (mirror ALWAYS wins — the
 * pre-lodgement intake is the source of truth for read_only fields).
 */
export function applyF80Mirror(state: FormState, mirror: FormState): FormState {
  const next: Record<string, unknown> = structuredClone(state) as Record<string, unknown>;
  for (const [path, value] of Object.entries(mirror)) {
    const parts = path.split('.');
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      if (typeof cur[k] !== 'object' || cur[k] === null || Array.isArray(cur[k])) cur[k] = {};
      cur = cur[k] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return next;
}
